/**
 * 将网页滚动到目标Dom位置
 * @param target 目标Dom元素
 * @param duration 滚动时长, 单位:毫秒
 * @param options 其它选项(offset, force, cancelable, onStart, onDone, onCancel)
 */
export default (function (){
	const NEWTON_ITERATIONS = 4;
	const NEWTON_MIN_SLOPE = 0.001;
	const SUBDIVISION_PRECISION = 0.0000001;
	const SUBDIVISION_MAX_ITERATIONS = 10;

	const kSplineTableSize = 11;
	const kSampleStepSize = 1.0 / (kSplineTableSize - 1.0);

	const float32ArraySupported = typeof Float32Array === 'function';

	const A = (aA1: number, aA2: number) => {
		return 1.0 - 3.0 * aA2 + 3.0 * aA1;
	};
	const B = (aA1: number, aA2: number) => {
		return 3.0 * aA2 - 6.0 * aA1;
	};
	const C = (aA1: number) => {
		return 3.0 * aA1;
	};

	// Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
	const calcBezier = (aT: number, aA1: number, aA2: number) => {
		return ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT;
	};

	// Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
	const getSlope = (aT: number, aA1: number, aA2: number) => {
		return 3.0 * A(aA1, aA2) * aT * aT + 2.0 * B(aA1, aA2) * aT + C(aA1);
	};

	const binarySubdivide = (aX: number, aA: number, aB: number, mX1: number, mX2: number) => {
		var currentX,
			currentT,
			i = 0;
		do {
			currentT = aA + (aB - aA) / 2.0;
			currentX = calcBezier(currentT, mX1, mX2) - aX;
			if (currentX > 0.0) {
				aB = currentT;
			} else {
				aA = currentT;
			}
		} while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
		return currentT;
	};

	const newtonRaphsonIterate = (aX: number, aGuessT: number, mX1: number, mX2: number) => {
		for (var i = 0; i < NEWTON_ITERATIONS; ++i) {
			var currentSlope = getSlope(aGuessT, mX1, mX2);
			if (currentSlope === 0.0) {
				return aGuessT;
			}
			var currentX = calcBezier(aGuessT, mX1, mX2) - aX;
			aGuessT -= currentX / currentSlope;
		}
		return aGuessT;
	};

	const LinearEasing = (x: any) => {
		return x;
	};

	const BezierEasing = (mX1: number, mY1: number, mX2: number, mY2: number) => {
		if (!(0 <= mX1 && mX1 <= 1 && 0 <= mX2 && mX2 <= 1)) {
			throw new Error('bezier x values must be in [0, 1] range');
		}

		if (mX1 === mY1 && mX2 === mY2) {
			return LinearEasing;
		}

		// Precompute samples table
		var sampleValues = float32ArraySupported ? new Float32Array(kSplineTableSize) : new Array(kSplineTableSize);
		for (var i = 0; i < kSplineTableSize; ++i) {
			sampleValues[i] = calcBezier(i * kSampleStepSize, mX1, mX2);
		}

		function getTForX (aX: number){
			var intervalStart = 0.0;
			var currentSample = 1;
			var lastSample = kSplineTableSize - 1;

			for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) {
				intervalStart += kSampleStepSize;
			}
			--currentSample;

			// Interpolate to provide an initial guess for t
			var dist =
				(aX - sampleValues[currentSample]) / (sampleValues[currentSample + 1] - sampleValues[currentSample]);
			var guessForT = intervalStart + dist * kSampleStepSize;

			var initialSlope = getSlope(guessForT, mX1, mX2);
			if (initialSlope >= NEWTON_MIN_SLOPE) {
				return newtonRaphsonIterate(aX, guessForT, mX1, mX2);
			} else if (initialSlope === 0.0) {
				return guessForT;
			} else {
				return binarySubdivide(aX, intervalStart, intervalStart + kSampleStepSize, mX1, mX2);
			}
		}

		return function BezierEasing (x: any){
			// Because JavaScript number are imprecise, we should guarantee the extremes are right.
			if (x === 0) {
				return 0;
			}
			if (x === 1) {
				return 1;
			}
			return calcBezier(getTForX(x), mY1, mY2);
		};
	};

	const easings = {
		ease: [ 0.25, 0.1, 0.25, 1.0 ],
		linear: [ 0.0, 0.0, 1.0, 1.0 ],
		'ease-in': [ 0.42, 0.0, 1.0, 1.0 ],
		'ease-out': [ 0.0, 0.0, 0.58, 1.0 ],
		'ease-in-out': [ 0.42, 0.0, 0.58, 1.0 ],
	};
	const abortEvents = [ 'mousedown', 'wheel', 'DOMMouseScroll', 'mousewheel', 'keyup', 'touchmove' ];

	let defaults = {
		container: 'body',
		duration: 500,
		easing: 'ease',
		offset: 0,
		offsetX: 0,
		offsetY: 0,
		force: true,
		cancelable: true,
		onStart: false,
		onDone: false,
		onCancel: false,
		x: false,
		y: true,
	};

	const setDefaults = (options: any) => {
		defaults = Object.assign({}, defaults, options);
	};

	const cumulativeOffset = (element: any) => {
		let top = 0;
		let left = 0;

		do {
			top += element.offsetTop || 0;
			left += element.offsetLeft || 0;
			element = element.offsetParent;
		} while (element);

		return {
			top: top,
			left: left,
		};
	};

	const scroller = () => {
		let element: any; // element to scroll to
		let container: any; // container to scroll
		let esaeDuration: number; // duration of the scrolling
		let easing: string; // easing to be used when scrolling
		let offsetX: number; // offset to be added (subtracted)
		let offsetY: number; // offset to be added (subtracted)
		let force: boolean; // force scroll, even if element is visible
		let cancelable: boolean; // indicates if user can cancel the scroll or not.
		let onStart: any; // callback when scrolling is started
		let onDone: any; // callback when scrolling is done
		let onCancel: any; // callback when scrolling is canceled / aborted
		let x: number; // scroll on x axis
		let y: number; // scroll on y axis

		let initialX: number; // initial X of container
		let targetX: number; // target X of container
		let initialY: number; // initial Y of container
		let targetY: number; // target Y of container
		let diffX: number; // difference
		let diffY: number; // difference

		let abort: boolean; // is scrolling aborted

		let abortEv: any; // event that aborted scrolling
		let abortFn = (e: any) => {
			if (!cancelable) return;
			abortEv = e;
			abort = true;
		};
		let easingFn: any;

		let timeStart: any; // time when scrolling started
		let timeElapsed: any; // time elapsed since scrolling started

		let progress: number; // progress

		function scrollTop (container: HTMLElement){
			let scrollTop = container.scrollTop;

			if (container.tagName.toLowerCase() === 'body') {
				// in firefox body.scrollTop always returns 0
				// thus if we are trying to get scrollTop on a body tag
				// we need to get it from the documentElement
				scrollTop = scrollTop || document.documentElement.scrollTop;
			}

			return scrollTop;
		}

		function scrollLeft (container: HTMLElement){
			let scrollLeft = container.scrollLeft;

			if (container.tagName.toLowerCase() === 'body') {
				// in firefox body.scrollLeft always returns 0
				// thus if we are trying to get scrollLeft on a body tag
				// we need to get it from the documentElement
				scrollLeft = scrollLeft || document.documentElement.scrollLeft;
			}

			return scrollLeft;
		}

		function step (timestamp: any){
			if (abort) return done();
			if (!timeStart) timeStart = timestamp;

			timeElapsed = timestamp - timeStart;

			progress = Math.min(timeElapsed / esaeDuration, 1);
			progress = easingFn(progress);

			topLeft(container, initialY + diffY * progress, initialX + diffX * progress);

			timeElapsed < esaeDuration ? window.requestAnimationFrame(step) : done();
		}

		function done (){
			if (!abort) topLeft(container, targetY, targetX);
			timeStart = false;

			for (let i = 0; i < abortEvents.length; i++) {
				container.removeEventListener(abortEvents[i], abortFn);
			}
			if (abort && onCancel) onCancel(abortEv, element);
			if (!abort && onDone) onDone(element);
		}

		function topLeft (element: HTMLElement, top: number, left: number){
			if (y) element.scrollTop = top;
			if (x) element.scrollLeft = left;
			if (element.tagName.toLowerCase() === 'body') {
				// in firefox body.scrollTop doesn't scroll the page
				// thus if we are trying to scrollTop on a body tag
				// we need to scroll on the documentElement
				if (y) document.documentElement.scrollTop = top;
				if (x) document.documentElement.scrollLeft = left;
			}
		}

		function scrollTo (target: string | HTMLElement, duration: number = 500, options = {}){
			(options as any).duration = duration;

			if ((target as any).style) {
				element = target;
			} else if (!(target as any).style && document.querySelector(String(target))) {
				element = document.querySelector(String(target));
			}

			if (!element) {
				return console.warn('找不到目标Dom元素, 无法进行滚动操作!');
			}

			container = document.querySelector((options as any).container || defaults.container);
			esaeDuration = (options as any).duration || defaults.duration;
			easing = (options as any).easing || defaults.easing;
			offsetX = (options as any).offsetX || (options as any).offsetX || defaults.offsetX || defaults.offset;
			offsetY = (options as any).offsetY || (options as any).offsetY || defaults.offsetY || defaults.offset;
			force = (options as any).hasOwnProperty('force') ? (options as any).force !== false : defaults.force;
			cancelable = (options as any).hasOwnProperty('cancelable')
				? (options as any).cancelable !== false
				: defaults.cancelable;
			onStart = (options as any).onStart || defaults.onStart;
			onDone = (options as any).onDone || defaults.onDone;
			onCancel = (options as any).onCancel || defaults.onCancel;
			x = (options as any).x === undefined ? defaults.x : (options as any).x;
			y = (options as any).y === undefined ? defaults.y : (options as any).y;

			var cumulativeOffsetContainer = cumulativeOffset(container);
			var cumulativeOffsetElement = cumulativeOffset(element);

			initialY = scrollTop(container);
			targetY = cumulativeOffsetElement.top - cumulativeOffsetContainer.top + offsetY;

			initialX = scrollLeft(container);
			targetX = cumulativeOffsetElement.left - cumulativeOffsetContainer.left + offsetX;

			abort = false;

			diffY = targetY - initialY;
			diffX = targetX - initialX;

			if (!force) {
				const containerTop = initialY;
				const containerBottom = containerTop + container.offsetHeight;
				const elementTop = targetY;
				const elementBottom = elementTop + (element as any).offsetHeight;
				if (elementTop >= containerTop && elementBottom <= containerBottom) {
					// make sure to call the onDone callback even if there is no need to
					// scroll the container. Fixes #111 (ref #118)
					onDone(element);
					return;
				}
			}

			easingFn = BezierEasing.apply(BezierEasing, (easings as any)[easing] || easings['ease']);

			if (!diffY && !diffX) return;
			if (onStart) onStart(element);

			let supportsPassive = false;
			try {
				let opts = Object.defineProperty({}, 'passive', {
					get:
						function (){
							supportsPassive = true;
						},
				});
				window.addEventListener('test', function (){}, opts);
			} catch (e) {}
			for (let i = 0; i < abortEvents.length; i++) {
				container.addEventListener(abortEvents[i], abortFn, supportsPassive ? { passive: true } : false);
			}

			window.requestAnimationFrame(step);

			return () => {
				abortEv = null;
				abort = true;
			};
		}

		return scrollTo;
	};
	let _scroller = scroller();
	(_scroller as any).setConfig = setDefaults;
	return _scroller;
})();
